import { arrayMove } from '@dnd-kit/sortable'
import { allFieldKeys, emptyState, newField, newSection } from './builderModel'
import { toFieldKey } from '../../utils/helpers'

export const initialBuilderState = { ...emptyState(), selected: { kind: 'form' }, dirty: false }

const findField = (sections, fieldUid) => {
  for (const section of sections) {
    const index = section.fields.findIndex((f) => f.uid === fieldUid)
    if (index >= 0) return { section, index }
  }
  return null
}

const mapField = (sections, fieldUid, fn) =>
  sections.map((s) => ({ ...s, fields: s.fields.map((f) => (f.uid === fieldUid ? fn(f) : f)) }))

export function builderReducer(state, action) {
  switch (action.type) {
    case 'load':
      // saved state gets fresh uids; keep the selection on the same field (by key) or section (by position)
      return { ...action.state, selected: carrySelection(state, action.state), dirty: false }

    case 'select':
      return { ...state, selected: action.selected }

    case 'setMeta':
      return { ...state, meta: { ...state.meta, ...action.patch }, dirty: true }

    case 'addSection': {
      const section = newSection()
      return { ...state, sections: [...state.sections, section], selected: { kind: 'section', uid: section.uid }, dirty: true }
    }

    case 'updateSection':
      return { ...state, sections: state.sections.map((s) => (s.uid === action.uid ? { ...s, ...action.patch } : s)), dirty: true }

    case 'removeSection':
      return { ...state, sections: state.sections.filter((s) => s.uid !== action.uid), selected: { kind: 'form' }, dirty: true }

    case 'moveSection': {
      const from = state.sections.findIndex((s) => s.uid === action.uid)
      const to = from + action.delta
      if (from < 0 || to < 0 || to >= state.sections.length) return state
      return { ...state, sections: arrayMove(state.sections, from, to), dirty: true }
    }

    case 'addField': {
      if (state.sections.length === 0) return state
      const field = newField(action.fieldType, allFieldKeys(state.sections))
      const sectionUid = action.sectionUid || state.sections[state.sections.length - 1].uid
      const sections = state.sections.map((s) => {
        if (s.uid !== sectionUid) return s
        const fields = [...s.fields]
        fields.splice(action.index ?? fields.length, 0, field)
        return { ...s, fields }
      })
      return { ...state, sections, selected: { kind: 'field', uid: field.uid }, dirty: true }
    }

    case 'updateField': {
      const sections = mapField(state.sections, action.uid, (f) => {
        const next = { ...f, ...action.patch }
        // key follows the label until the user edits the key (never for saved fields)
        if (action.patch.label !== undefined && !f.keyTouched) {
          const base = toFieldKey(action.patch.label) || 'field'
          const taken = allFieldKeys(state.sections, f.uid)
          let key = base
          let n = 2
          while (taken.has(key)) key = `${base}${n++}`
          next.fieldKey = key
        }
        if (action.patch.fieldKey !== undefined) next.keyTouched = true
        return next
      })
      return { ...state, sections, dirty: true }
    }

    case 'removeField':
      return {
        ...state,
        sections: state.sections.map((s) => ({ ...s, fields: s.fields.filter((f) => f.uid !== action.uid) })),
        selected: { kind: 'form' },
        dirty: true,
      }

    case 'moveField': {
      // toIndex is the position in the target section's list *before* removal of the dragged field
      const source = findField(state.sections, action.uid)
      if (!source) return state
      const field = source.section.fields[source.index]
      if (source.section.uid === action.toSectionUid) {
        const to = Math.min(action.toIndex, source.section.fields.length - 1)
        if (to === source.index) return state
        return {
          ...state,
          sections: state.sections.map((s) => (s.uid === action.toSectionUid ? { ...s, fields: arrayMove(s.fields, source.index, to) } : s)),
          dirty: true,
        }
      }
      const sections = state.sections.map((s) => {
        if (s.uid === source.section.uid) return { ...s, fields: s.fields.filter((f) => f.uid !== action.uid) }
        if (s.uid === action.toSectionUid) {
          const fields = [...s.fields]
          fields.splice(Math.min(action.toIndex, fields.length), 0, field)
          return { ...s, fields }
        }
        return s
      })
      return { ...state, sections, dirty: true }
    }

    default:
      return state
  }
}

function carrySelection(previous, next) {
  const { selected } = previous
  if (selected?.kind === 'field') {
    const location = findField(previous.sections, selected.uid)
    const key = location?.section.fields[location.index].fieldKey
    const match = next.sections.flatMap((s) => s.fields).find((f) => f.fieldKey === key)
    if (match) return { kind: 'field', uid: match.uid }
  }
  if (selected?.kind === 'section') {
    const index = previous.sections.findIndex((s) => s.uid === selected.uid)
    if (next.sections[index]) return { kind: 'section', uid: next.sections[index].uid }
  }
  return { kind: 'form' }
}

export const findFieldLocation = findField
